import { mockSectionData, type MockSectionCustomerInput } from './mockSectionData.util';
import { coerceBhConnectionCount, coerceBhConnectionNumber } from './bhConnectionCoerce.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE } from './whoWereReachingSnapshotDetect.util';

/** First month shown on PHQ-9 / GAD-7 trend charts. */
export const CLINICAL_OUTCOMES_CHART_MONTH_MIN = 1;

/** Last month shown on PHQ-9 / GAD-7 trend charts. */
export const CLINICAL_OUTCOMES_CHART_MONTH_MAX = 6;

/** PHQ-9 / GAD-7 score at or below which symptoms are considered minimal. */
export const CLINICAL_OUTCOMES_MINIMAL_THRESHOLD = 5;

/** Clinical goal category row for the outcomes section. */
export interface ClinicalCareOutcomesGoalRow {
  category: string;
  count: number;
  percent: number;
}

/**
 * Builds clinical goal category rows from warehouse aggregates.
 * @param rows - `vw_monthly_customer_clinical_goals` rows.
 * @returns Sorted goal rows (count descending).
 */
export const buildClinicalCareOutcomesGoalsFromRows = (
  rows: Array<{
    category?: unknown;
    identified_clinical_goals?: unknown;
    pct_of_total?: unknown;
  }>,
): ClinicalCareOutcomesGoalRow[] =>
  rows
    .map(row => {
      const category =
        typeof row.category === 'string' ? row.category.trim() : '';
      const count = Math.trunc(
        coerceBhConnectionNumber(row.identified_clinical_goals),
      );
      const pctRaw = coerceBhConnectionNumber(row.pct_of_total);
      const percent =
        pctRaw > 0 && pctRaw <= 1
          ? Math.round(pctRaw * 100)
          : Math.round(pctRaw);
      if (!category || count <= 0) {
        return null;
      }
      return { category, count, percent };
    })
    .filter((row): row is ClinicalCareOutcomesGoalRow => row !== null)
    .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category));

/**
 * Sums identified clinical goals across category rows for the headline stat.
 * @param rows - `vw_monthly_customer_clinical_goals` rows.
 * @returns Total identified goals.
 */
export const sumClinicalCareOutcomesTreatmentGoals = (
  rows: Array<{ identified_clinical_goals?: unknown }>,
): number =>
  rows.reduce(
    (sum, row) => sum + coerceBhConnectionCount(row.identified_clinical_goals),
    0,
  );

/** PHQ-9 / GAD-7 trend point keyed by months in BH treatment. */
export interface ClinicalCareOutcomesTrendPoint {
  monthsInTreatment: number;
  phq9: number;
  gad7: number;
}

const hasTreatmentScore = (value: unknown): boolean =>
  coerceBhConnectionNumber(value) > 0 ||
  (typeof value === 'number' && Number.isFinite(value) && value >= 0);

/**
 * Builds PHQ-9 and GAD-7 series from `bh_member_month` aggregates (months 1–6).
 * @param rows - Treatment-duration aggregate rows.
 * @returns Ordered trend points for chart months 1–6.
 */
export const buildClinicalCareOutcomesTrend = (
  rows: Array<{
    agg_months_in_bh_treatment?: unknown;
    avg_phq9_score?: unknown;
    avg_gad7_score?: unknown;
  }>,
): ClinicalCareOutcomesTrendPoint[] =>
  rows
    .map(row => ({
      monthsInTreatment: Math.trunc(
        coerceBhConnectionCount(row.agg_months_in_bh_treatment),
      ),
      phq9: Math.round(coerceBhConnectionNumber(row.avg_phq9_score) * 10) / 10,
      gad7: Math.round(coerceBhConnectionNumber(row.avg_gad7_score) * 10) / 10,
    }))
    .filter(
      row =>
        row.monthsInTreatment >= CLINICAL_OUTCOMES_CHART_MONTH_MIN &&
        row.monthsInTreatment <= CLINICAL_OUTCOMES_CHART_MONTH_MAX &&
        (hasTreatmentScore(row.phq9) || hasTreatmentScore(row.gad7)),
    )
    .sort((a, b) => a.monthsInTreatment - b.monthsInTreatment);

const buildChartMonths = (trend: ClinicalCareOutcomesTrendPoint[]): string[] =>
  trend.map(row => `Month ${row.monthsInTreatment}`);

/**
 * Builds the clinical-care-outcomes snapshot from warehouse rows.
 * @param treatmentRows - `bh_member_month` aggregate rows.
 * @param customer - Report customer context.
 * @param reportingPeriod - Human-readable report period label.
 * @param clinicalGoalRows - `vw_monthly_customer_clinical_goals` rows.
 * @param metricRow - Current-period `vw_monthly_customer_reporting_metric` row.
 * @returns Snapshot object or null when warehouse data is empty.
 */
export const buildClinicalCareOutcomesSnapshotFromRows = (
  treatmentRows: Array<{
    agg_months_in_bh_treatment?: unknown;
    avg_phq9_score?: unknown;
    avg_gad7_score?: unknown;
  }>,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
  clinicalGoalRows: Array<{
    category?: unknown;
    identified_clinical_goals?: unknown;
    pct_of_total?: unknown;
  }> = [],
  metricRow: {
    ct_mem_ever_in_care?: unknown;
    ct_mem_current_in_care?: unknown;
  } | null = null,
): Record<string, unknown> | null => {
  const trend = buildClinicalCareOutcomesTrend(treatmentRows);
  const clinicalGoals = buildClinicalCareOutcomesGoalsFromRows(clinicalGoalRows);
  const membersInClinicalCare = coerceBhConnectionCount(
    metricRow?.ct_mem_ever_in_care,
  );
  const activeThisMonth = coerceBhConnectionCount(
    metricRow?.ct_mem_current_in_care,
  );
  const treatmentGoalsIdentified =
    sumClinicalCareOutcomesTreatmentGoals(clinicalGoalRows);

  const hasWarehouseData =
    membersInClinicalCare > 0 ||
    activeThisMonth > 0 ||
    treatmentGoalsIdentified > 0 ||
    trend.length > 0 ||
    clinicalGoals.length > 0;

  if (!hasWarehouseData) {
    return null;
  }

  const mockBase = mockSectionData(
    'clinical-care-outcomes',
    customer,
    reportingPeriod,
  );

  const months = buildChartMonths(trend);
  const phq9Series = trend.map(row => row.phq9);
  const gad7Series = trend.map(row => row.gad7);

  const fieldSources: ReportSectionFieldSources = {
    membersInClinicalCare: membersInClinicalCare > 0 ? 'warehouse' : 'mock',
    activeThisMonth: activeThisMonth > 0 ? 'warehouse' : 'mock',
    treatmentGoalsIdentified:
      treatmentGoalsIdentified > 0 ? 'warehouse' : 'mock',
    phq9Series: trend.length > 0 ? 'warehouse' : 'mock',
    gad7Series: trend.length > 0 ? 'warehouse' : 'mock',
    months: trend.length > 0 ? 'warehouse' : 'mock',
    clinicalGoals: clinicalGoals.length > 0 ? 'warehouse' : 'mock',
    insights: 'mock',
  };

  return {
    ...mockBase,
    snapshotSource: WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
    membersInClinicalCare,
    activeThisMonth,
    treatmentGoalsIdentified,
    months: months.length > 0 ? months : mockBase.months,
    phq9Series: phq9Series.length > 0 ? phq9Series : mockBase.phq9Series,
    gad7Series: gad7Series.length > 0 ? gad7Series : mockBase.gad7Series,
    treatmentTrend: trend,
    clinicalGoals,
    minimalThreshold: CLINICAL_OUTCOMES_MINIMAL_THRESHOLD,
    fieldSources,
  };
};
